import { useEffect, useState } from 'react';
import type { EstadoUsuario, Nivel, ReclamacionesSesion, Rol } from '@discopico/domain';
import { obtenerSupabase } from '../supabase/cliente.js';

const ROLES: readonly Rol[] = ['operario', 'jefe_operarios', 'contador', 'administrador'];
const ESTADOS: readonly EstadoUsuario[] = ['activo', 'inactivo', 'bloqueado'];

/**
 * Decodifica la carga de un token sin verificar la firma.
 *
 * Eso NO es un descuido. Lo único que este dato decide es qué controles se muestran.
 * Alguien que se fabrique un token con rol de administrador va a ver el menú del
 * administrador y va a recibir 403 en cada llamada, porque la firma la verifica
 * Supabase y el estado lo revalida `app.assert_puede_operar` contra la tabla.
 * Verificar la firma acá daría una falsa sensación de seguridad sin cambiar nada.
 */
function decodificarCarga(token: string): Record<string, unknown> | null {
  const partes = token.split('.');
  if (partes.length !== 3) return null;
  // El largo ya garantiza que exista, pero `noUncheckedIndexedAccess` no lo deduce del
  // condicional de arriba y esta configuración está encendida a propósito en este
  // proyecto. Se comprueba en vez de afirmarlo con un `!`.
  const cargaCruda = partes[1];
  if (cargaCruda === undefined) return null;
  try {
    const base64 = cargaCruda.replace(/-/g, '+').replace(/_/g, '/');
    const relleno = base64 + '==='.slice((base64.length + 3) % 4);
    const texto = JSON.parse(atob(relleno)) as unknown;
    return typeof texto === 'object' && texto !== null ? (texto as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Lee las seis reclamaciones que el gancho de token puso en `app_metadata`.
 *
 * Devuelve nulo, y nunca un objeto a medias, cuando falta algo o cuando un valor no
 * está en el contrato. Un objeto a medias produce una interfaz que muestra el menú de
 * un rol que no existe; un nulo manda a la pantalla de ingreso, que es recuperable.
 *
 * Lee **solo** de `app_metadata`. Si alguien mueve el gancho a la raíz de las
 * reclamaciones, esta función devuelve nulo y la sesión no arranca. Es a propósito: en
 * la raíz `app.rol()` de la base tampoco las vería, así que una interfaz que funcionara
 * de todos modos estaría escondiendo el problema.
 */
export function leerReclamaciones(accessToken: string | null | undefined): ReclamacionesSesion | null {
  if (!accessToken) return null;

  const carga = decodificarCarga(accessToken);
  const meta = carga?.app_metadata;
  if (typeof meta !== 'object' || meta === null) return null;

  const m = meta as Record<string, unknown>;
  const rol = m.rol;
  const estado = m.estado;
  const nivel = m.nivel;
  const usuarioId = m.usuario_id;

  if (typeof rol !== 'string' || !ROLES.includes(rol as Rol)) return null;
  if (typeof estado !== 'string' || !ESTADOS.includes(estado as EstadoUsuario)) return null;
  if (typeof nivel !== 'number' || ![0, 1, 2, 3].includes(nivel)) return null;
  if (typeof usuarioId !== 'string' || usuarioId === '') return null;

  return {
    usuarioId,
    rol: rol as Rol,
    sedeId: typeof m.sede_id === 'string' ? m.sede_id : null,
    estado: estado as EstadoUsuario,
    nivel: nivel as Nivel,
    debeCambiarPassword: m.debe_cambiar_password === true,
    superAdministrador: m.super_administrador === true,
    puedeElegirRol: m.puede_elegir_rol === true,
    ...(typeof m.rol_propio === 'string' && ROLES.includes(m.rol_propio as Rol)
      ? { rolPropio: m.rol_propio as Rol }
      : {}),
  };
}

/**
 * La vista que le corresponde a cada rol, que es la línea
 * `Y le asigna la vista correspondiente a su rol "<rol>"` del esquema de escenario.
 *
 * El operario y el jefe de operarios comparten grupo porque comparten pantallas: lo que
 * los diferencia son los controles de dentro, y eso lo decide `puede`.
 *
 * EL ADMINISTRADOR ENTRA AL TABLERO Y NO A `/direccion`. La revisión de UX del 2026-09-28 lo
 * encontró aterrizando en una lista de tarjetas que repetía el menú, sin una sola cifra, cuando lo
 * primero que el dueño quiere ver al abrir es cómo va el mes. El tablero vive en el mismo grupo, así
 * que sigue siendo «la vista de dirección» que nombra la especificación; `/direccion` queda como la
 * entrada «Inicio» del menú.
 */
export function rutaInicialDe(rol: Rol | null | undefined): string {
  switch (rol) {
    case 'operario':
    case 'jefe_operarios':
      return '/operacion';
    case 'contador':
      return '/contabilidad';
    case 'administrador':
      return '/tablero';
    default:
      return '/ingresar';
  }
}

/** Estado de sesión para los componentes. `cargando` evita el parpadeo del primer render. */
export type EstadoSesion = { sesion: ReclamacionesSesion | null; cargando: boolean };

export function useSesion(): EstadoSesion {
  const [estado, setEstado] = useState<EstadoSesion>({ sesion: null, cargando: true });

  useEffect(() => {
    let vivo = true;

    // El cliente se pide acá adentro y no en el cuerpo del módulo. Así importar este
    // archivo no construye nada, que es lo que permite que `sesion.test.ts` pruebe
    // `leerReclamaciones` y `rutaInicialDe` sin variables de entorno ni Supabase.
    const supabase = obtenerSupabase();

    supabase.auth.getSession().then(({ data }) => {
      if (!vivo) return;
      setEstado({ sesion: leerReclamaciones(data.session?.access_token), cargando: false });
    });

    // Cada renovación de token trae reclamaciones frescas del gancho, así que un cambio
    // de rol o un bloqueo se refleja en la interfaz al refrescar el token y no solo al
    // volver a entrar.
    const { data: suscripcion } = supabase.auth.onAuthStateChange((_evento, sesion) => {
      if (!vivo) return;
      setEstado({ sesion: leerReclamaciones(sesion?.access_token), cargando: false });
    });

    return () => {
      vivo = false;
      suscripcion.subscription.unsubscribe();
    };
  }, []);

  return estado;
}
